import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { Observable, ReplaySubject, of } from 'rxjs';
import { debounceTime, switchMap, take } from 'rxjs/operators';
import { BaseComponent } from 'src/app/core/components/base.component';

@Component({
  selector: 'app-autocomplete-select',
  templateUrl: './autocomplete-select.component.html',
  styleUrls: ['./autocomplete-select.component.scss']
})
export class AutocompleteSelectComponent extends BaseComponent implements OnInit {
  @Input() _formControl!: FormControl;
  @Input() getItemName!: (item: any) => any;
  @Input() search!: (value: string | null, loadedItems?: number) => Observable<any[] | null>;
  @Input() debounceTime: number = 300;
  @Output() selected = new EventEmitter<any | null>();
  @Input() label: string | null = null;
  @Input() errors: string[] | null = null;
  @Input() readOnly: boolean = false;
  @Input() minCharacters: number = 0;
  @Input() showDivider: (item: any) => boolean = () => false;
  @Input() class?: string | null = null;
  @Input() allowNonMatchingItem: boolean = false;

  protected readonly filteredItems = new ReplaySubject<any[] | null>(1);
  private readonly valueChanges = new ReplaySubject<string | null>(1);
  private wasSelected: boolean = false;

  constructor() {
    super();
  }

  ngOnInit() {
    // Use a subject that is only changed by user input
    this.valueChanges
      .pipe(
        debounceTime(this.debounceTime),
        switchMap(value => {
          if ((value?.length ?? 0) < this.minCharacters) {
            return of(null);
          }

          return this.search(value!);
        })
      )
      .unsubscribeOnDestroy(this)
      .subscribe(items => {
        if (items) {
          this.filteredItems.next(items);
        }
      });
  }

  protected onItemSelected(event: MatAutocompleteSelectedEvent): void {
    this.selected.emit(event.option.value);
    this.wasSelected = true;
  }

  protected getDisplayName = (item: any) => {
    return this.getItemName(item);
  }

  protected onRemoveSelectedItem(): void {
    this._formControl.setValue(null);
    this.selected.emit(null);
  }

  protected onKeyUp(event: any): void {
    this.valueChanges.next(event.target.value);
    this.wasSelected = false;
  }

  protected onFocus() {
    if (!this._formControl.value) {
      this.valueChanges.next(null);
    }
  }

  protected onBlur() {
    if(!this.allowNonMatchingItem) {
      this.removeNonMatchingItem();
    }
  }

  private removeNonMatchingItem(): void {
    // If an item was just selected, we don't need to run this check
    if(this.wasSelected) {
      return;
    }

    this.filteredItems.pipe(take(1))
      .unsubscribeOnDestroy(this)
      .subscribe(items => {
        // If the text doesn't exactly match one item, remove it
        if (items?.length !== 1) {
          this._formControl.setValue(null);

          return;
        }

        if(typeof this._formControl.value !== 'string') {
          return;
        }

        const typedItem = items?.find(item => this.getItemName(item)?.toLowerCase() === this._formControl.value?.toLowerCase());

        // If the text matches an item name, select this item
        if (typedItem) {
          this._formControl.setValue(typedItem);
          this.selected.emit(typedItem);
          this.wasSelected = true;
        }
        else {
          // Otherwise remove the non-matching free text
          this._formControl.setValue(null);
        }
      });
  }
}
